import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Spacer,
  Text,
} from '@chakra-ui/react';
import React from 'react';

import { VerificationPipelineStatusGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { StatusBadge } from './StatusBadge';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import {
  BreadcrumbProps,
  Breadcrumbs,
} from 'packages/common-frontend/components/Breadcrumbs';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

export const reportingDateQueryParam = 'reportingDate';
export const baseNewVerificationUrl = '/funding-requests/verifications/new';

type VerificationsTemplateProps = {
  children: React.ReactNode;
  status: VerificationPipelineStatusGQLType | null;
  selectedCreditFacilityId: string | null;
  selectedReportingDate: string | null;
};
export const VerificationsTemplate = ({
  children,
  status,
  selectedCreditFacilityId,
  selectedReportingDate,
}: VerificationsTemplateProps) => {
  const { displayOptions } = useVerificationOptions();

  if (!displayOptions.showTopLevelHeader) {
    return <>{children}</>;
  }

  const breadcrumbs: BreadcrumbProps[] = [
    {
      label: 'Funding Request',
      path: '/funding-requests/',
    },
    {
      label: 'Verification',
    },
  ];

  const areCreditFacilityAndReportingDatePreselected =
    selectedCreditFacilityId && selectedReportingDate && status !== null;

  const initiateNewPipelineButtonLabel = areCreditFacilityAndReportingDatePreselected
    ? 'Upload New Version'
    : 'Start Verification';

  const queryParams = `?${creditFacilityIdQueryParam}=${selectedCreditFacilityId}&${reportingDateQueryParam}=${selectedReportingDate}`;
  const initiateNewPipelineButtonLink = areCreditFacilityAndReportingDatePreselected
    ? `${baseNewVerificationUrl}${queryParams}`
    : baseNewVerificationUrl;

  return (
    <Box>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Flex mt="16px">
        <Box>
          <Flex>
            <Heading as="h2" mr="20px">
              Verify Funding Request
            </Heading>
            {status && <StatusBadge value={status} />}
          </Flex>
        </Box>
        <Spacer />
        <Box>
          {/* Use Link instead of onClick to allow easy opening in new tab. */}
          <Link href={initiateNewPipelineButtonLink}>
            <Button
              colorScheme="aurora"
              borderWidth={
                areCreditFacilityAndReportingDatePreselected ? 'medium' : 'none'
              }
              variant={
                areCreditFacilityAndReportingDatePreselected
                  ? 'outline'
                  : 'solid'
              }
            >
              {initiateNewPipelineButtonLabel}
            </Button>
          </Link>
        </Box>
      </Flex>
      <Text pt="16px">
        Use this workflow to verify and approve funding requests.
      </Text>
      <Box>{children}</Box>
    </Box>
  );
};

type VerificationsAccordionProps = {
  header: string;
  icon: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  isExpanded: boolean;
  isExpandedBackgroundColor?: string;
  children: React.ReactNode;
  overflow?: React.ComponentProps<typeof Accordion>['overflow'];
};
export const VerificationsAccordion = ({
  header,
  icon,
  isExpanded,
  isExpandedBackgroundColor,
  children,
  overflow,
}: VerificationsAccordionProps) => {
  return (
    <Accordion
      allowToggle
      border="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
      index={isExpanded ? 0 : undefined}
      backgroundColor={isExpanded ? isExpandedBackgroundColor : undefined}
      sx={
        overflow
          ? { '& .chakra-collapse': { overflow: `${overflow}!important` } }
          : {}
      }
    >
      <AccordionItem border="none" m="32px">
        <AccordionButton>
          {icon}
          <Box width="8px" />
          <Heading as="h4" size="md">
            {header}
          </Heading>
          <Spacer />
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel mt="16px">{children}</AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
